import { ref, computed, Ref } from "vue";

export type MeditationType =
  | "Deep & Slow Looking"
  | "Virtual Session"
  | "In-Gallery Session"
  | "In-Museum Session"
  | "Art Mindfully"
  | "Contemplative Exploration"
  | "Interview/Conversation"
  | "Talks"
  | "Perspective"
  | "Shared Experience"
  | "Navigation"
  | "Primer Virtual Session"
  | "Primer Live Experience";

// Length filter options, represented as max duration in minutes
export type LengthFilter = 5 | 10 | 15 | 20;

// Filter keys (only these two for now)
export type FilterKey = "meditationType" | "audio.length";

// Typed filters object: key to allowed values arrays
export type Filters = {
  meditationType?: MeditationType[];
  "audio.length"?: LengthFilter[];
};

export type FilterOption = {
  key: string;
  value: any;
};

export type SortOption = {
  key: string;
  direction: "asc" | "desc";
};

export function useFilters<T>(items: T[] | Ref<T[]>) {
  // activeFilters: map from filter key to list of allowed values
  const activeFilters = ref<Filters>({});

  const currentSort = ref<SortOption | null>(null);

  // Helper to get nested values like "artist.name"
  const getNestedValue = (obj: any, path: string): any =>
    path.split(".").reduce((acc, part) => acc?.[part], obj);

  // Example parseAudioLength stays the same
  const parseAudioLength = (length: string): number =>
    parseInt(length.match(/\d+/)?.[0] ?? "0", 10);

  const filteredItems = computed(() => {
    let filtered = Array.isArray(items) ? items : items.value;

    // Apply filters
    const filterKeys = Object.keys(activeFilters.value);
    if (filterKeys.length) {
      filtered = filtered.filter((item) =>
        filterKeys.every((key) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const filterValues = activeFilters.value[key];
          if (!filterValues || filterValues.length === 0) return true;

          const itemValue = getNestedValue(item, key);

          // For length (or other numeric filters), you might want to customize matching:
          if (key === "audio.length") {
            const itemLength = parseAudioLength(itemValue);
            // Assuming filterValues contains max length thresholds as numbers
            // Match if itemLength <= any of the selected max lengths
            return filterValues.some((maxLen: number) => itemLength <= maxLen);
          }

          // For string matching (e.g. meditationType), check if itemValue is in filterValues
          return filterValues.includes(itemValue);
        })
      );
    }

    // Apply sorting
    if (currentSort.value) {
      const { key, direction } = currentSort.value;
      filtered = [...filtered].sort((a, b) => {
        let aVal = getNestedValue(a, key);
        let bVal = getNestedValue(b, key);

        if (key === "audio.length") {
          aVal = parseAudioLength(aVal);
          bVal = parseAudioLength(bVal);
        }

        return direction === "asc"
          ? aVal > bVal
            ? 1
            : aVal < bVal
            ? -1
            : 0
          : aVal < bVal
          ? 1
          : aVal > bVal
          ? -1
          : 0;
      });
    }

    return filtered;
  });

  // Apply filters for a given key; overwrite existing values for that filter key
  const applyFilter = (key: string, values: any[]) => {
    activeFilters.value = {
      ...activeFilters.value,
      [key]: values,
    };
  };

  // Remove entire filter by key
  const removeFilter = (key: string) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { [key]: _, ...rest } = activeFilters.value;
    activeFilters.value = rest;
  };

  const clearFilters = () => {
    activeFilters.value = {};
  };

  const setSorting = (key: string, direction: "asc" | "desc" = "asc") => {
    currentSort.value = { key, direction };
  };

  const clearSorting = () => {
    currentSort.value = null;
  };

  const toggleSorting = (key: string) => {
    if (currentSort.value?.key === key) {
      // Flip direction if same key is clicked again
      const newDirection =
        currentSort.value.direction === "asc" ? "desc" : "asc";
      currentSort.value = { key, direction: newDirection };
    } else {
      // New key starts with ascending sort
      currentSort.value = { key, direction: "asc" };
    }
  };

  return {
    filteredItems,
    activeFilters,
    currentSort,
    applyFilter,
    removeFilter,
    clearFilters,
    setSorting,
    clearSorting,
    toggleSorting,
  };
}
